import { FC, useMemo, useRef, useState } from 'react';
import {
  agregarResumo,
  categoriasPadrao,
  coberturaBaixa,
  fechadoAte,
  distribuicaoFaixas,
  filtrarLinhas,
  mesPorChave,
  serieDiaria,
  topDesvios,
  type FiltroFaixa,
  type ModoTopDesvio,
} from '@/utils/equiplexSop';
import type { SopPayload } from '@/types/dashboards';
import { formatosVigentes } from '@/utils/filtroPaineis';
import { resumoPorLinhaDoPeriodo } from '@/utils/equiplexSopLinhas';
import type { CoresPainel } from '../chartTheme';
import DesviosTable from './DesviosTable';
import FaixasDesvioCard from './FaixasDesvioCard';
import FaturamentoChart from './FaturamentoChart';
import ProducaoDiariaChart from './ProducaoDiariaChart';
import ProducaoPorLinhaTable from './ProducaoPorLinhaTable';
import ProgramadoProduzidoChart from './ProgramadoProduzidoChart';
import ResumoMensalTable from './ResumoMensalTable';
import SopAvisos from './SopAvisos';
import SopKpis from './SopKpis';
import TopDesviosChart from './TopDesviosChart';

interface PainelSopProps {
  payload: SopPayload;
  cores: CoresPainel;
}

/** Os meses do período do filtro (o servidor já recortou); sem mês passado, o último. */
const mesesDoPeriodo = (payload: SopPayload): string[] => {
  const validos = payload.meses_padrao.filter((k) => payload.meses.some((m) => m.key === k));
  return validos.length ? validos : payload.meses.slice(-1).map((m) => m.key);
};

/**
 * Os meses dos blocos mês a mês (volume, faturamento, produzido por linha): o período e os de
 * antes dele que o servidor mandou (até 6, desde 27/10/2026), menos os futuros. Snapshot de antes
 * só tem o período.
 */
const mesesDoHistorico = (payload: SopPayload): string[] => payload.meses.filter((m) => !m.futuro).map((m) => m.key);

/**
 * O painel em si, com os filtros locais (formato, tabela). Período e produto vêm do filtro dos
 * painéis, aplicado no servidor. Remonta (via `key`) quando chega snapshot novo; com filtro novo
 * o payload muda e os filtros locais ficam.
 */
const PainelSop: FC<PainelSopProps> = ({ payload, cores }) => {
  const mesesSel = useMemo(() => mesesDoPeriodo(payload), [payload]);
  // `null` = os formatos padrão do período; a escolha da pessoa vale enquanto algum dela existir
  // no filtro novo (trocar de produto pode tirar do período o formato que ela marcou).
  const [escolhidas, setEscolhidas] = useState<string[] | null>(null);
  const categorias = useMemo(
    () => formatosVigentes(escolhidas, payload.categorias_volume, () => categoriasPadrao(payload, mesesSel)),
    [escolhidas, payload, mesesSel],
  );
  const [modoTop, setModoTop] = useState<ModoTopDesvio>('unidades');
  const [busca, setBusca] = useState('');
  const [mesTabela, setMesTabela] = useState('todos');
  const [faixa, setFaixa] = useState<FiltroFaixa>('todas');
  const tabelaRef = useRef<HTMLDivElement>(null);

  const porChave = useMemo(() => mesPorChave(payload), [payload]);
  const mesesHist = useMemo(() => mesesDoHistorico(payload), [payload]);
  const resumosHist = useMemo(() => payload.resumo_mensal.filter((r) => mesesHist.includes(r.mes)), [payload, mesesHist]);
  const porLinha = useMemo(() => resumoPorLinhaDoPeriodo(payload.resumo_por_linha, mesesHist), [payload, mesesHist]);
  const agregado = useMemo(() => agregarResumo(payload.resumo_mensal, mesesSel), [payload, mesesSel]);
  const cobBaixa = useMemo(() => coberturaBaixa(payload, mesesSel), [payload, mesesSel]);
  const serie = useMemo(() => serieDiaria(payload, mesesSel, categorias), [payload, mesesSel, categorias]);
  const top = useMemo(() => topDesvios(payload.linhas, mesesSel, modoTop), [payload, mesesSel, modoTop]);
  const faixas = useMemo(() => distribuicaoFaixas(payload.linhas, mesesSel), [payload, mesesSel]);
  const mesesTabela = useMemo(
    () => (mesTabela !== 'todos' && mesesSel.includes(mesTabela) ? [mesTabela] : mesesSel),
    [mesTabela, mesesSel],
  );
  const linhasTabela = useMemo(
    () => filtrarLinhas(payload, { meses: mesesTabela, busca, faixa }),
    [payload, mesesTabela, busca, faixa],
  );
  const mesAberto = payload.meses.find((m) => m.em_andamento && mesesSel.includes(m.key));
  const fechado = fechadoAte(payload);

  const alternarCategoria = (c: string) =>
    setEscolhidas(categorias.includes(c) ? categorias.filter((x) => x !== c) : [...categorias, c]);

  const filtrarPorFaixa = (f: FiltroFaixa) => {
    setFaixa(f);
    setMesTabela('todos');
    tabelaRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  };

  return (
    <>
      <SopAvisos
        frescor={payload.frescor}
        cobertura={cobBaixa}
        mesEmAndamento={mesAberto}
        meses={porChave}
        fechadoAte={fechado}
      />
      <SopKpis resumo={agregado} coberturaBaixa={cobBaixa.length > 0} fechadoAte={mesAberto ? fechado : null} />

      {/* Um gráfico por linha (26/10/2026): largura toda, mais alto, com os números à vista. */}
      <ProgramadoProduzidoChart resumos={resumosHist} periodo={mesesSel} fechadoAte={fechado} cores={cores} />
      <FaturamentoChart resumos={resumosHist} cobertura={payload.cobertura_faturamento} cores={cores} />
      <ProducaoDiariaChart
        serie={serie}
        categorias={payload.categorias_volume}
        selecionadas={categorias}
        onAlternar={alternarCategoria}
        meses={payload.meses.filter((m) => mesesSel.includes(m.key))}
        corte={payload.corte}
        cores={cores}
      />
      <FaixasDesvioCard distribuicao={faixas} limiares={payload.limiares} modo={cores.modo} onFiltrar={filtrarPorFaixa} />

      <TopDesviosChart top={top} modo={modoTop} onModo={setModoTop} itens={payload.itens} meses={porChave} cores={cores} />

      <div ref={tabelaRef} className="scroll-mt-6">
        <DesviosTable
          linhas={linhasTabela}
          itens={payload.itens}
          meses={payload.meses}
          mesesSelecionados={mesesSel}
          busca={busca}
          onBusca={setBusca}
          mesTabela={mesTabela}
          onMesTabela={setMesTabela}
          faixa={faixa}
          onFaixa={setFaixa}
          modo={cores.modo}
          fechadoAte={fechado}
        />
      </div>

      <ResumoMensalTable
        resumos={payload.resumo_mensal}
        meses={payload.meses}
        selecionados={mesesSel}
        cobertura={payload.cobertura_faturamento}
      />

      <ProducaoPorLinhaTable resumos={porLinha} meses={payload.meses} />

      <details className="rounded-xl border border-theme-border bg-surface-card p-5 text-sm" data-testid="sop-notas">
        <summary className="cursor-pointer font-semibold text-content-primary">Como os números são calculados</summary>
        <ul className="mt-3 list-disc space-y-1 pl-5 text-content-secondary">
          {payload.notas.map((n) => (
            <li key={n}>{n}</li>
          ))}
          <li>
            Faixas: crítico acima de {payload.limiares.critico}% de desvio, alerta acima de {payload.limiares.alerta}%. Fonte:
            réplica MySQL do Datasul (ems2mov/ems2cad), atualizada todo dia às 06:00.
          </li>
        </ul>
      </details>
    </>
  );
};

export default PainelSop;
